import { useState, useEffect, useCallback } from 'preact/hooks';
import Button from '../components/shared/Button.jsx';
import Card from '../components/shared/Card.jsx';
import InvoicePaidModal from '../components/InvoicePaidModal.jsx';
import { formatEuros, formatDate } from '../lib/format.js';

const STATUS_LABEL = { open: 'Open', paid: 'Betaald', canceled: 'Ingetrokken' };

export default function InvoicesView({ onChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [preview, setPreview] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [date, setDate] = useState(today());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [paidTarget, setPaidTarget] = useState(null);

  const refresh = useCallback(async (forDate) => {
    try {
      const [invoicesRes, previewRes, receivablesRes] = await Promise.all([
        fetch('/api/invoices', { credentials: 'same-origin' }),
        fetch(`/api/invoices/run/preview?date=${forDate}`, { credentials: 'same-origin' }),
        fetch(`/api/invoices/receivables?date=${forDate}`, { credentials: 'same-origin' })
      ]);
      if (invoicesRes.ok) setInvoices((await invoicesRes.json()).invoices || []);
      if (previewRes.ok) setPreview(await previewRes.json());
      if (receivablesRes.ok) setReceivables(await receivablesRes.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(date); }, [refresh, date]);

  async function runNow() {
    if (!preview?.canRun || preview.invoiceCount === 0) return;
    if (!confirm(
      `${preview.invoiceCount} ${preview.invoiceCount === 1 ? 'factuur' : 'facturen'} opstellen ` +
      `voor in totaal ${formatEuros(preview.totalCents)}? Factuurnummers worden definitief uitgegeven.`
    )) return;
    setBusy(true);
    try {
      const res = await fetch('/api/invoices/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ date })
      });
      const data = res.headers.get('content-type')?.includes('application/json') ? await res.json() : {};
      if (!res.ok) {
        setNotice(data.error === 'billing_entity_incomplete'
          ? `De facturerende partij is nog niet compleet: ${(data.missing || []).join(', ')}.`
          : 'De run is mislukt.');
        return;
      }
      const failed = data.failed?.length
        ? ` ${data.failed.length} mislukt: ${data.failed.map(f => f.debtorName).join(', ')}.`
        : '';
      setNotice(`${data.created.length} ${data.created.length === 1 ? 'factuur' : 'facturen'} opgesteld: ${data.created.map(c => c.number).join(', ')}.${failed}`);
      refresh(date);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  }

  async function act(invoice, path, body) {
    const res = await fetch(`/api/invoices/${invoice.id}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body || {})
    });
    if (!res.ok) {
      setNotice(`Actie op ${invoice.number} is mislukt.`);
      return;
    }
    refresh(date);
  }

  function cancel(invoice) {
    if (!confirm(`${invoice.number} intrekken? Het nummer blijft bestaan — een gat in de reeks mag niet.`)) return;
    act(invoice, 'cancel');
  }

  return (
    <div class="invoices-tab">
      <div class="dashboard-section-head">
        <h2>Facturen</h2>
        <label class="inline-field">
          <span class="muted small">Peildatum</span>
          <input type="date" value={date} onInput={e => setDate(e.currentTarget.value)} />
        </label>
      </div>

      {notice && (
        <div class="inline-notice" role="status">
          {notice}
          <button type="button" class="inline-notice-close" onClick={() => setNotice(null)} aria-label="Melding sluiten">&times;</button>
        </div>
      )}

      {receivables && receivables.openCount > 0 && (
        <Card>
          <div class="receivables">
            <div>
              <div class="muted small">Openstaand</div>
              <div class="receivables-amount">{formatEuros(receivables.openCents)}</div>
              <div class="muted small">{receivables.openCount} {receivables.openCount === 1 ? 'factuur' : 'facturen'}</div>
            </div>
            {receivables.overdueCount > 0 && (
              <div>
                <div class="muted small">Waarvan vervallen</div>
                <div class="receivables-amount receivables-overdue">{formatEuros(receivables.overdueCents)}</div>
                <div class="muted small">{receivables.overdueCount} te laat</div>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card title="Facturatierun">
        {/* Why: wie er niet gefactureerd wordt is belangrijker dan wie wel. Een
            debiteur die stilletjes uit de run valt is precies de fout die pas
            maanden later opvalt. */}
        {preview?.skippedCount > 0 && (
          <div class="notice notice-warning">
            <strong>
              Bij {preview.skippedCount} {preview.skippedCount === 1 ? 'debiteur' : 'debiteuren'} worden
              abonnementsregels ingehouden.
            </strong>{' '}
            Het aantal gebruikers of afdelingen is daar niet betrouwbaar opgehaald, dus er
            wordt niet gefactureerd op mogelijk verouderde cijfers. Losse posten gaan wel door:
            die zijn een vast bedrag en hangen niet van een telling af.
            <ul class="skipped-list">
              {preview.skipped.map(d => (
                <li key={d.debtorId}>
                  <strong>{d.debtorName}</strong> ({formatEuros(d.amountCents)} ingehouden
                  {d.partial ? '; de losse posten staan wel op de factuur' : '; geen factuur'})
                  <ul>
                    {d.problems.map((pr, i) => (
                      <li key={i}>{pr.productName}: {pr.reason}{pr.detail ? ` (${pr.detail})` : ''}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            Los dit op via het tabblad Ophaalstatus en draai de run daarna opnieuw.
          </div>
        )}
        {!preview ? (
          <div class="empty-state muted">Laden…</div>
        ) : !preview.canRun ? (
          <div class="form-warning">
            <strong>Nog niet mogelijk.</strong> Vul eerst {preview.blockedBy.join(', ')} in
            bij <strong>Instellingen</strong>. Zonder die gegevens komt er geen geldige factuur uit.
          </div>
        ) : preview.invoiceCount === 0 ? (
          <div class="empty-state">
            <p>Niets te factureren op {formatDate(date)}.</p>
            <p class="muted">Zet de peildatum vooruit om te zien wat er later aankomt.</p>
          </div>
        ) : (
          <>
            <p class="muted small">
              Op {formatDate(date)} staan {preview.lineCount} {preview.lineCount === 1 ? 'periode' : 'perioden'} klaar,
              samen goed voor {preview.invoiceCount} {preview.invoiceCount === 1 ? 'factuur' : 'facturen'}.
              Meerdere abonnementen van dezelfde debiteur komen op één factuur.
            </p>
            <div class="table-scroll">
              <table class="data-table">
                <thead>
                  <tr><th>Debiteur</th><th>Regels</th><th>Excl. BTW</th><th>BTW</th><th>Totaal</th></tr>
                </thead>
                <tbody>
                  {preview.debtors.map(d => (
                    <tr key={d.debtorId}>
                      <td>{d.debtorName}</td>
                      <td class="muted">
                        {d.lines.map(l => (
                          <div key={`${l.subscriptionId}-${l.periodStart}`} class="preview-line">
                            {l.description} · {formatDate(l.periodStart)} t/m {formatDate(l.periodEnd)}
                          </div>
                        ))}
                      </td>
                      <td>{formatEuros(d.subtotalCents)}</td>
                      <td class="muted">{formatEuros(d.vatCents)}</td>
                      <td><strong>{formatEuros(d.totalCents)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div class="form-actions">
              <Button onClick={runNow} disabled={busy}>
                {busy ? 'Bezig…' : `Facturen opstellen (${formatEuros(preview.totalCents)})`}
              </Button>
            </div>
          </>
        )}
      </Card>

      {loading ? (
        <Card><div class="empty-state muted">Facturen laden…</div></Card>
      ) : invoices.length === 0 ? (
        <Card>
          <div class="empty-state">
            <p>Nog geen facturen opgesteld.</p>
          </div>
        </Card>
      ) : (
        <Card title={`Opgesteld (${invoices.length})`}>
          <div class="table-scroll">
            <table class="data-table">
              <thead>
                <tr><th>Nummer</th><th>Debiteur</th><th>Datum</th><th>Vervalt</th><th>Totaal</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {invoices.map(inv => [
                  <tr key={inv.id} class={inv.status === 'canceled' ? 'row-muted' : ''}>
                    <td>
                      <button type="button" class="link-like" onClick={() => setExpanded(expanded === inv.id ? null : inv.id)}>
                        {inv.number}
                      </button>
                    </td>
                    <td>{inv.debtor.name}</td>
                    <td class="muted">{formatDate(inv.issueDate)}</td>
                    <td class={inv.status === 'open' && inv.dueDate < date ? 'usage-over' : 'muted'}>
                      {formatDate(inv.dueDate)}
                    </td>
                    <td>{formatEuros(inv.totalCents)}</td>
                    <td><span class={`status-chip status-chip-${inv.status === 'paid' ? 'active' : inv.status === 'open' ? 'requested' : ''}`}>{STATUS_LABEL[inv.status]}</span></td>
                    <td class="row-actions">
                      <a class="btn btn-ghost btn-sm" href={`/api/invoices/${inv.id}/pdf`} target="_blank" rel="noopener">PDF</a>
                      {inv.status === 'open' && <Button size="sm" onClick={() => setPaidTarget(inv)}>Betaald</Button>}
                      {inv.status === 'paid' && <Button size="sm" variant="ghost" onClick={() => act(inv, 'unpaid')}>Toch open</Button>}
                      {inv.status === 'open' && <Button size="sm" variant="ghost" onClick={() => cancel(inv)}>Intrekken</Button>}
                    </td>
                  </tr>,
                  expanded === inv.id && (
                    <tr key={`${inv.id}-detail`} class="detail-row">
                      <td colSpan={7}>
                        <div class="invoice-detail">
                          <div>
                            <strong>{inv.issuer.legalName}</strong>
                            {inv.issuer.tradeName && <span class="muted"> h/o {inv.issuer.tradeName}</span>}
                            <div class="muted small">
                              KvK {inv.issuer.kvkNr} · BTW {inv.issuer.btwNr} · {inv.issuer.iban}
                            </div>
                          </div>
                          <table class="data-table">
                            <thead>
                              <tr><th>Omschrijving</th><th>Periode</th><th>Bedrag</th></tr>
                            </thead>
                            <tbody>
                              {inv.lines.map(l => (
                                <tr key={l.id}>
                                  <td>{l.description}</td>
                                  <td class="muted">{formatDate(l.periodStart)} t/m {formatDate(l.periodEnd)}</td>
                                  <td>{formatEuros(l.amountCents)}</td>
                                </tr>
                              ))}
                              <tr>
                                <td colSpan={2} class="muted">Subtotaal excl. BTW</td>
                                <td>{formatEuros(inv.subtotalCents)}</td>
                              </tr>
                              <tr>
                                <td colSpan={2} class="muted">BTW {inv.vatRatePercent}%</td>
                                <td>{formatEuros(inv.vatCents)}</td>
                              </tr>
                              <tr>
                                <td colSpan={2}><strong>Totaal</strong></td>
                                <td><strong>{formatEuros(inv.totalCents)}</strong></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )
                ])}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <InvoicePaidModal
        isOpen={!!paidTarget}
        invoice={paidTarget}
        onClose={() => setPaidTarget(null)}
        onConfirm={async paidOn => {
          await act(paidTarget, 'paid', { paid_on: paidOn });
          setPaidTarget(null);
        }}
      />
    </div>
  );
}

function today() {
  return new Date().toISOString().slice(0, 10);
}
